import React from "react";
import {
  C, Label, Draw, Worker, DataCenter, Pylon, pylonArms,
  Arm, armCycle, armTip, taper, ARM_HITS, ARM_POSES,
  clamp, lerp, seg, smooth, ease, mixColor, rng, wire,
} from "../kit";

// Act IV · The Cloud (the second verse and chorus). Almost entirely cold.
// A bootprint in the dirt is poured over with concrete; on the hill above the
// old town the complex is raised and switched on, a castle floating on its
// own fog, and his open, calloused hand rises dark against it. Inside, the
// arms work a lights-out hall with nobody in it. Out at the fence he fades
// away while the machines blink on at exactly the same pace.
//
// Shots start at p = .00 pour · .075 castle (the hand rises at p ~.30) · .52
// hall (a hard cut) · .80 fence; the others dissolve in. Each shot keys its
// story beats to q, its own 0 -> 1; blinking, fans, hum and fog run on
// seconds. Every SVG id starts with "cl-".

// Tall phones letterbox far past BLEED, so backgrounds run further still.
const EXT = { x: -400, y: -1800, w: 2400, h: 4500 };

const frac = (v) => v - Math.floor(v);
const noise = (n) => frac(Math.sin(n * 127.1 + 311.7) * 43758.5453);
const f1 = (v) => (Math.round(v * 10) / 10).toString();
const line = (list) => list.map(([x, y], i) => `${i ? "L" : "M"} ${f1(x)} ${f1(y)}`).join(" ");
const poly = (list) => line(list) + " Z";
const oval = (cx, cy, rx, ry) =>
  `M ${f1(cx - rx)} ${f1(cy)} a ${f1(rx)} ${f1(ry)} 0 1 0 ${f1(2 * rx)} 0 a ${f1(rx)} ${f1(ry)} 0 1 0 ${f1(-2 * rx)} 0 `;
const sq = (x, y, s) => `M ${f1(x)} ${f1(y)} h ${f1(s)} v ${f1(s)} h ${f1(-s)} Z `;

// ---------------------------------------------------------------------------
// Shot 1 · The pour. One bootprint in the dirt; wet concrete rolls over it
// and keeps coming.
// ---------------------------------------------------------------------------

const BOOT = { x: 930, y: 470, a: -16 };
const BOOT_D =
  "M 0 -210 C 64 -212 82 -130 78 -62 C 75 -6 52 28 50 70 C 48 108 64 138 62 174 " +
  "C 60 204 32 212 0 212 C -30 212 -56 204 -58 174 C -60 138 -42 108 -45 70 " +
  "C -48 30 -74 -4 -75 -62 C -78 -132 -62 -208 0 -210 Z";
// Raised soil left between the boot's lugs: chevrons split by the centre
// channel up front, bars under the heel.
const LUGS_D = (() => {
  let d = "";
  for (let y = -178; y <= 30; y += 28) {
    d += `M -70 ${y + 12} L -9 ${y - 4} M 9 ${y - 4} L 70 ${y + 12} `;
  }
  for (let y = 100; y <= 196; y += 30) d += `M -64 ${y} L -8 ${y} M 8 ${y} L 64 ${y} `;
  return d;
})();
const SOIL = (() => {
  const r = rng(4040);
  let light = "";
  let dark = "";
  let cracks = "";
  let straw = "";
  for (let i = 0; i < 90; i++) {
    const x = -340 + r() * 2280;
    const y = -120 + r() * 1060;
    const rx = 1.5 + Math.pow(r(), 2) * 9;
    const s = oval(x, y, rx, rx * (0.55 + r() * 0.35));
    if (r() < 0.55) light += s;
    else dark += s;
  }
  for (let i = 0; i < 16; i++) {
    let x = -300 + r() * 2200;
    let y = -60 + r() * 960;
    cracks += `M ${f1(x)} ${f1(y)}`;
    for (let j = 0; j < 4; j++) {
      x += (r() - 0.5) * 90;
      y += 10 + r() * 40;
      cracks += ` L ${f1(x)} ${f1(y)}`;
    }
    cracks += " ";
  }
  for (let i = 0; i < 22; i++) {
    const x = -300 + r() * 2200;
    const y = -60 + r() * 960;
    const a = r() * Math.PI;
    const l = 14 + r() * 30;
    straw += `M ${f1(x)} ${f1(y)} l ${f1(Math.cos(a) * l)} ${f1(Math.sin(a) * l)} `;
  }
  return { light, dark, cracks, straw };
})();

// The leading edge of the pour at x = X: broad lobes, slowly churning.
function pourEdge(X, local) {
  const pts = [];
  for (let y = -1900; y <= 2800; y += 40) {
    const lobe = 36 * Math.sin(y * 0.009 + 1.3 + local * 0.4) + 15 * Math.sin(y * 0.024 - local * 0.6);
    pts.push([X + lobe, y]);
  }
  return pts;
}
// A smooth path through points, cornering on quadratic curves.
function smoothPath(pts) {
  let d = `M ${f1(pts[0][0])} ${f1(pts[0][1])}`;
  for (let i = 1; i < pts.length - 1; i++) {
    d += ` Q ${f1(pts[i][0])} ${f1(pts[i][1])} ${f1((pts[i][0] + pts[i + 1][0]) / 2)} ${f1((pts[i][1] + pts[i + 1][1]) / 2)}`;
  }
  const e = pts[pts.length - 1];
  return `${d} L ${f1(e[0])} ${f1(e[1])}`;
}

function PourShot({ q, local }) {
  // It rolls in fast and slows like anything thick: it reaches the print
  // around q .45, has swallowed it by .7, and keeps creeping.
  const X = 560 + 1280 * Math.exp(-2.4 * seg(q, 0.08, 1));
  const edge = pourEdge(X, local);
  const front = smoothPath(edge);
  const body = `${front} L 2200 2800 L 2200 -1900 Z`;
  const wet = `${smoothPath(edge.map(([x, y]) => [x - 44, y]))} L 2200 2800 L 2200 -1900 Z`;
  const sheen = lerp(-500, 1500, frac(local * 0.12));
  const zoom = lerp(1.02, 1.1, smooth(clamp(q)));
  const cam = `translate(800 450) scale(${zoom.toFixed(4)}) translate(-800 -450)`;
  return (
    <g>
      <defs>
        <radialGradient id="cl-pr-light" cx="0.35" cy="0.3" r="0.8">
          <stop offset="0" stopColor="#3d2b1c" />
          <stop offset="1" stopColor="#110b07" />
        </radialGradient>
        <linearGradient id="cl-pr-slab" gradientUnits="userSpaceOnUse" x1="0" y1="-200" x2="900" y2="1100">
          <stop offset="0" stopColor="#50575e" />
          <stop offset="0.6" stopColor="#363d44" />
          <stop offset="1" stopColor="#22282d" />
        </linearGradient>
        <linearGradient id="cl-pr-sheen" gradientUnits="userSpaceOnUse" x1={sheen - 300} y1="0" x2={sheen + 300} y2="300">
          <stop offset="0" stopColor={C.coldHot} stopOpacity="0" />
          <stop offset="0.5" stopColor={C.coldHot} stopOpacity="0.14" />
          <stop offset="1" stopColor={C.coldHot} stopOpacity="0" />
        </linearGradient>
        <clipPath id="cl-pr-boot">
          <path d={BOOT_D} />
        </clipPath>
      </defs>
      <g transform={cam}>
        <rect x={EXT.x} y={EXT.y} width={EXT.w} height={EXT.h} fill="#110b07" />
        <rect x={-400} y={-300} width={2400} height={1500} fill="url(#cl-pr-light)" />
        <path d={SOIL.dark} fill="#070504" opacity={0.7} />
        <path d={SOIL.light} fill="#4e3a28" opacity={0.6} />
        <path d={SOIL.cracks} stroke="#050302" strokeWidth={1.6} fill="none" opacity={0.7} />
        <path d={SOIL.straw} stroke="#7a6038" strokeWidth={1.6} fill="none" opacity={0.45} strokeLinecap="round" />

        {/* His bootprint: a pressed hollow, a lip of pushed-up soil, the tread */}
        <g transform={`translate(${BOOT.x} ${BOOT.y}) rotate(${BOOT.a})`}>
          <path d={BOOT_D} fill="none" stroke="#5a4330" strokeWidth={14} opacity={0.55} transform="translate(-3 -4)" />
          <path d={BOOT_D} fill="#0d0906" />
          <g clipPath="url(#cl-pr-boot)">
            <path d={BOOT_D} fill="#22170e" transform="translate(7 9)" />
            <path d={LUGS_D} stroke="#120c08" strokeWidth={15} fill="none" strokeLinecap="round" transform="translate(-2 -3)" />
            <path d={LUGS_D} stroke="#3b2a1b" strokeWidth={10} fill="none" strokeLinecap="round" />
            <path d="M -60 70 Q 0 58 54 70" stroke="#2a1d12" strokeWidth={22} fill="none" />
          </g>
        </g>

        {/* The pour rolls over it */}
        <path d={wet} fill="#050302" opacity={0.5} />
        <path d={body} fill="url(#cl-pr-slab)" />
        <path d={body} fill="url(#cl-pr-sheen)" />
        <path d={smoothPath(edge.map(([x, y]) => [x + 16, y + 8]))} stroke="#161b20" strokeWidth={14} fill="none" opacity={0.45} />
        <path d={front} stroke="#8d969e" strokeWidth={5} fill="none" opacity={0.8} />
        <path d={smoothPath(edge.map(([x, y]) => [x - 2, y - 3]))} stroke={C.coldHot} strokeWidth={1.4} fill="none" opacity={0.45} />
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// The castle: the old town at the foot of the hill and the complex on top,
// floating on its own fog. Shared by the castle shot and the fence shot.
// ---------------------------------------------------------------------------

const PLAT = 410; // the plateau
const HUB = [1004, PLAT - 104]; // where the hum rings start
const HILL_D =
  `M -400 772 L -100 760 C 120 746 270 684 380 600 C 440 540 480 462 524 ${PLAT + 2} L 1476 ${PLAT + 2} ` +
  "C 1520 462 1560 540 1620 600 C 1700 670 1800 706 2000 716 V 2700 H -400 Z";
const BLOCKS = [
  { id: "cl-dc-l", x: 560, y: PLAT - 4, w: 320, h: 150, s: 0.9, b: [0.06, 0.24], label: "SITE 7 · B" },
  { id: "cl-dc-r", x: 1150, y: PLAT - 4, w: 320, h: 150, s: 0.92, b: [0.1, 0.27], label: "SITE 7 · C" },
  { id: "cl-dc-c", x: 740, y: PLAT + 8, w: 600, h: 230, s: 0.88, b: [0.03, 0.22], label: "SITE 7" },
];
const TOWERS = [
  { x: 612, h: 262, w: 64, b: [0.1, 0.24] },
  { x: 1418, h: 242, w: 60, b: [0.14, 0.28] },
];
const MASTS = [{ x: 688, h: 300 }, { x: 1332, h: 340 }];
const CRANES = [
  { x: 880, height: 330, jib: 240, counter: 78, dir: 1, ph: 0.4 },
  { x: 1190, height: 300, jib: 210, counter: 70, dir: -1, ph: 2.3 },
];
// Work lights on the site while it goes up.
const LAMPS = [[640, PLAT - 16], [905, PLAT - 6], [1160, PLAT - 12], [1390, PLAT - 16]];
const pylonRun = (list, at0) => list.map(([x, y, s], i) => ({ x, y, s, at: at0 + i * 0.035 }));
const RUN_L = pylonRun([[236, 766, 1.2], [336, 690, 0.95], [414, 616, 0.76], [472, 544, 0.6], [514, 482, 0.48]], 0.08);
const RUN_R = pylonRun([[1620, 750, 1.14], [1530, 674, 0.9], [1466, 600, 0.72], [1428, 536, 0.58], [1402, 478, 0.47]], 0.11);
const GANTRY_L = [[566, 364], [582, 360], [598, 364], [568, 380], [582, 377], [596, 380]];
const GANTRY_R = [[1408, 364], [1424, 360], [1440, 364], [1410, 380], [1424, 377], [1438, 380]];
const FOG = [
  { x: 1000, y: PLAT + 14, rx: 1000, ry: 30, o: 0.85, sp: 0.05, ph: 0 },
  { x: 620, y: PLAT + 38, rx: 780, ry: 42, o: 0.7, sp: -0.035, ph: 2 },
  { x: 1380, y: PLAT + 52, rx: 740, ry: 38, o: 0.65, sp: 0.04, ph: 4 },
  { x: 960, y: PLAT + 88, rx: 1180, ry: 50, o: 0.5, sp: -0.025, ph: 1 },
  { x: 460, y: PLAT + 130, rx: 640, ry: 34, o: 0.32, sp: 0.03, ph: 3 },
  { x: 1520, y: PLAT + 136, rx: 560, ry: 30, o: 0.3, sp: -0.03, ph: 5 },
];
// Cloud tops along the deck: lit from above, soft underneath.
const BILLOWS = (() => {
  const r = rng(2024);
  let d = "";
  for (let x = 240; x < 1780; x += 44 + r() * 42) {
    const rr = 22 + r() * 38;
    d += oval(x, PLAT + 30 + r() * 16, rr * 1.6, rr);
  }
  return d;
})();
// The access road switchbacks up from the town, lit cold all the way.
const ROAD_PTS = [[700, 760], [930, 716], [760, 668], [1050, 620], [860, 566], [1110, 520], [960, 478], [1010, PLAT + 6]];
const ROAD_D = (() => {
  let d = `M ${ROAD_PTS[0][0]} ${ROAD_PTS[0][1]}`;
  for (let i = 1; i < ROAD_PTS.length; i++) {
    const [x0, y0] = ROAD_PTS[i - 1];
    const [x1, y1] = ROAD_PTS[i];
    d += ` C ${f1(x0 + (x1 - x0) * 0.6)} ${y0} ${f1(x1 - (x1 - x0) * 0.6)} ${y1} ${x1} ${y1}`;
  }
  return d;
})();
const ROAD_LAMPS = (() => {
  let d = "";
  for (let i = 1; i < ROAD_PTS.length; i++) {
    const [x0, y0] = ROAD_PTS[i - 1];
    const [x1, y1] = ROAD_PTS[i];
    for (let k = 0.2; k < 1; k += 0.3) {
      const x = lerp(x0, x1, k);
      const y = lerp(y0, y1, k) - 6;
      d += oval(x, y, 1.8, 1.8);
    }
  }
  return d;
})();
const CLOUDS = [
  { x: 420, y: 150, rx: 520, ry: 20, sp: 6 },
  { x: 1300, y: 96, rx: 440, ry: 15, sp: -5 },
  { x: 940, y: 40, rx: 600, ry: 12, sp: 4 },
];
const STARS = (() => {
  const r = rng(731);
  const groups = ["", "", ""];
  for (let i = 0; i < 54; i++) {
    groups[i % 3] += `M ${f1(-300 + r() * 2200)} ${f1(-900 + r() * 1080)} h 0.01 `;
  }
  return groups;
})();

// The town along the foot of the hill: one silhouette and three window groups.
const TOWN_Y = 752;
const TOWN = (() => {
  const r = rng(907);
  let d = "";
  const wins = ["", "", ""];
  let x = -420;
  while (x < 2020) {
    const w = 26 + r() * 46;
    const h = 18 + r() * 34;
    const gable = r() < 0.55;
    const top = TOWN_Y - h;
    d += gable
      ? `M ${f1(x)} ${TOWN_Y} V ${f1(top)} L ${f1(x + w / 2)} ${f1(top - w * 0.32)} L ${f1(x + w)} ${f1(top)} V ${TOWN_Y} Z `
      : `M ${f1(x)} ${TOWN_Y} V ${f1(top)} H ${f1(x + w)} V ${TOWN_Y} Z `;
    for (let wy = top + 6; wy < TOWN_Y - 8; wy += 10) {
      for (let wx = x + 5; wx < x + w - 8; wx += 9) {
        if (r() < 0.36) wins[Math.floor(r() * 3)] += `M ${f1(wx)} ${f1(wy)} h 4.5 v 5.5 h -4.5 Z `;
      }
    }
    x += w + (r() < 0.3 ? 8 + r() * 30 : 0);
  }
  // Steeple, water tower, elevator.
  d += `M 410 ${TOWN_Y} V ${TOWN_Y - 70} L 422 ${TOWN_Y - 128} L 434 ${TOWN_Y - 70} V ${TOWN_Y} Z `;
  d += `M 1160 ${TOWN_Y - 96} H 1206 V ${TOWN_Y - 80} L 1183 ${TOWN_Y - 70} L 1160 ${TOWN_Y - 80} Z `;
  d += `M 1500 ${TOWN_Y} V ${TOWN_Y - 104} H 1534 V ${TOWN_Y} Z M 1534 ${TOWN_Y} V ${TOWN_Y - 74} H 1556 V ${TOWN_Y} Z `;
  const legs = `M 1164 ${TOWN_Y - 74} L 1158 ${TOWN_Y} M 1202 ${TOWN_Y - 74} L 1208 ${TOWN_Y} M 1161 ${TOWN_Y - 40} H 1205`;
  return { d, wins, legs };
})();

function Crane({ x, base, height, jib, counter, dir, rise, local, ph, ink, beacon }) {
  if (rise <= 0) return null;
  const top = base - height * rise;
  const mw = 6;
  let mast = `M ${x - mw} ${base} V ${f1(top)} M ${x + mw} ${base} V ${f1(top)}`;
  for (let y = base; y - 12 > top; y -= 12) mast += ` M ${x - mw} ${f1(y)} L ${x + mw} ${f1(y - 12)}`;
  const arm = seg(rise, 0.88, 1);
  const slew = Math.cos(0.55 * Math.sin(local * 0.21 + ph));
  const J = jib * slew * dir * arm;
  const K = -counter * slew * dir * arm;
  const jy = top - 4;
  let truss = `M ${x} ${f1(jy)} L ${f1(x + J)} ${f1(jy)} M ${x} ${f1(jy + 10)} L ${f1(x + J)} ${f1(jy + 4)}`;
  const n = Math.max(1, Math.floor(Math.abs(J) / 14));
  for (let i = 0; i < n; i++) {
    truss += ` M ${f1(x + (J * i) / n)} ${f1(jy + 10 - (6 * i) / n)} L ${f1(x + (J * (i + 1)) / n)} ${f1(jy)}`;
  }
  const u = 0.45 + 0.3 * Math.sin(local * 0.37 + ph);
  const tx = x + J * u;
  const drop = 60 + 60 * (0.5 + 0.5 * Math.sin(local * 0.29 + ph * 1.7));
  const blink = frac(local * 0.8 + ph) < 0.1;
  return (
    <g stroke={ink} fill="none" strokeLinecap="round">
      <path d={mast} strokeWidth={1.6} />
      {arm > 0 && (
        <g opacity={arm}>
          <path d={truss} strokeWidth={1.4} />
          <path d={`M ${x} ${f1(jy)} L ${f1(x + K)} ${f1(jy + 2)}`} strokeWidth={4} />
          <rect x={f1(x + K * 0.9 - 9)} y={f1(jy - 2)} width={18} height={14} fill={ink} stroke="none" />
          <path d={`M ${x - mw} ${f1(jy)} L ${x} ${f1(jy - 30)} L ${x + mw} ${f1(jy)} M ${x} ${f1(jy - 30)} L ${f1(x + J * 0.62)} ${f1(jy)} M ${x} ${f1(jy - 30)} L ${f1(x + K)} ${f1(jy)}`} strokeWidth={1} />
          <path d={`M ${f1(tx)} ${f1(jy + 6)} V ${f1(jy + drop)}`} strokeWidth={0.8} />
          <rect x={f1(tx - 22)} y={f1(jy + drop)} width={44} height={5} fill={ink} stroke="none" />
          <circle cx={f1(x + J)} cy={f1(jy)} r={2.2} fill={beacon} stroke="none" opacity={blink ? 1 : 0.15} />
        </g>
      )}
    </g>
  );
}

// Six wires from one set of attachment points to the next, drawn far to near.
function Span({ from, to, sag, k, color, width }) {
  if (k <= 0) return null;
  const ds = from.map((a, i) => wire(a[0], a[1], to[i][0], to[i][1], sag));
  if (k >= 1) return <path d={ds.join(" ")} stroke={color} strokeWidth={width} fill="none" />;
  return <g>{ds.map((d, i) => <Draw key={i} d={d} p={k} stroke={color} strokeWidth={width} />)}</g>;
}

// A run of pylons climbing the hill; the first span comes from off-frame.
function Run({ pylons, gantry, built, ink, wireColor }) {
  const out = [];
  for (let i = pylons.length - 1; i >= 0; i--) {
    const py = pylons[i];
    const grow = ease.out(seg(built, py.at, py.at + 0.03));
    if (grow <= 0) continue;
    const arms = pylonArms(py.x, py.y, py.s);
    const prev = i === 0
      ? pylonArms(lerp(py.x, pylons[1].x, -1.4), lerp(py.y, pylons[1].y, -1.4), py.s * 1.3)
      : pylonArms(pylons[i - 1].x, pylons[i - 1].y, pylons[i - 1].s);
    out.push(
      <g key={`p${i}`} transform={`translate(0 ${py.y}) scale(1 ${grow.toFixed(3)}) translate(0 ${-py.y})`}>
        <Pylon x={py.x} y={py.y} s={py.s} color={ink} sw={py.s > 0.6 ? 1.8 : 1.2} />
      </g>,
      <Span key={`w${i}`} from={prev} to={arms} sag={6 + 12 * py.s} k={seg(built, py.at + 0.015, py.at + 0.05)} color={wireColor} width={0.9} />,
    );
  }
  const last = pylons[pylons.length - 1];
  out.push(
    <Span key="land" from={pylonArms(last.x, last.y, last.s)} to={gantry} sag={6}
      k={seg(built, last.at + 0.04, last.at + 0.08)} color={wireColor} width={0.9} />,
  );
  return <g>{out}</g>;
}

function CoolingTower({ x, h, w, rise, base, local, power, ink, rim, i }) {
  if (rise <= 0) return null;
  const hh = h * ease.out(rise);
  const top = base - hh;
  const d = `M ${x - w} ${base} Q ${f1(x - w * 0.42)} ${f1(base - hh * 0.62)} ${f1(x - w * 0.62)} ${f1(top)} L ${f1(x + w * 0.62)} ${f1(top)} Q ${f1(x + w * 0.42)} ${f1(base - hh * 0.62)} ${x + w} ${base} Z`;
  const puffs = [];
  if (power > 0 && rise >= 1) {
    for (let k = 0; k < 5; k++) {
      const age = frac(local / 7 + k / 5 + i * 0.37);
      const px = x + Math.sin(local * 0.2 + k * 1.7 + i) * 8 + age * 70;
      const py = top - age * 210;
      puffs.push(
        <circle key={k} cx={f1(px)} cy={f1(py)} r={f1(w * (0.55 + age * 1.5))} fill="url(#cl-steam)"
          opacity={power * 0.85 * Math.sin(age * Math.PI) * seg(age, 0, 0.15)} />,
      );
    }
  }
  return (
    <g>
      {puffs}
      <path d={d} fill={ink} stroke={rim} strokeWidth={1.4} strokeOpacity={0.5} />
      <path d={`M ${f1(x - w * 0.62)} ${f1(top + 2)} H ${f1(x + w * 0.62)}`} stroke={C.coldHot} strokeWidth={2} opacity={0.5 * power} />
    </g>
  );
}

function Mast({ x, h, base, k, ink, t }) {
  if (k <= 0) return null;
  const hh = h * ease.out(k);
  const top = base - hh;
  let brace = "";
  for (let y = base; y - 18 > top; y -= 18) {
    const w0 = 9 * ((y - top) / hh);
    const w1 = 9 * ((y - 18 - top) / hh);
    brace += `M ${f1(x - w0)} ${f1(y)} L ${f1(x + w1)} ${f1(y - 18)} M ${f1(x + w0)} ${f1(y)} L ${f1(x - w1)} ${f1(y - 18)} `;
  }
  const on = frac(t * 0.5 + x * 0.001) < 0.12;
  return (
    <g>
      <path d={`M ${x - 9} ${base} L ${x} ${f1(top)} L ${x + 9} ${base}`} stroke={ink} strokeWidth={1.8} fill="none" />
      <path d={brace} stroke={ink} strokeWidth={0.9} fill="none" />
      <circle cx={x} cy={f1(top)} r={on ? 3.2 : 2} fill={C.coldHot} opacity={on ? 1 : 0.25} />
      {on && <circle cx={x} cy={f1(top)} r={16} fill="url(#cl-beacon)" />}
    </g>
  );
}

// built: construction 0 -> 1; glow: the power; hum: ring strength;
// brown: the town's brownout; dim: how many of its windows are gone.
function Castle({ built, glow, hum, brown, dim, local, t, near = false }) {
  const ink = "#03050a";
  const rimCol = mixColor("#1a2430", C.cold, glow);
  const rings = hum > 0 ? [0, 1, 2].map((i) => {
    const age = frac(t / 3.2 + i / 3);
    return { age, rx: lerp(300, 1050, ease.out(age)), k: hum * Math.pow(1 - age, 1.6) * seg(age, 0, 0.05) };
  }) : [];
  const fogLit = mixColor("#2a3440", "#9fb4c8", glow);
  const wire2 = mixColor("#1b2530", "#2c3e50", glow);
  return (
    <g>
      {/* Sky, stars, clouds lit from below */}
      <rect x={EXT.x} y={EXT.y} width={EXT.w} height={2600} fill="url(#cl-sky)" />
      {STARS.map((d, i) => (
        <path key={i} d={d} stroke={C.ink[7]} strokeWidth={1.6 - i * 0.3} strokeLinecap="round"
          opacity={0.18 + 0.22 * (0.5 + 0.5 * Math.sin(local * 1.3 + i * 2.1))} />
      ))}
      <ellipse cx={HUB[0]} cy={HUB[1] + 60} rx={1150} ry={600} fill="url(#cl-dome)" opacity={0.2 + glow} />
      {CLOUDS.map((c, i) => (
        <ellipse key={i} cx={f1(c.x + Math.sin(local * 0.03 + i) * 30 + local * c.sp * 0.2)} cy={c.y} rx={c.rx} ry={c.ry}
          fill="url(#cl-cloud)" opacity={0.3 + 0.6 * glow} />
      ))}

      {/* The hill */}
      <path d={HILL_D} fill="url(#cl-hill)" />
      <path d={`M 524 ${PLAT + 2} L 1476 ${PLAT + 2}`} stroke={rimCol} strokeWidth={2} opacity={0.6} />

      {rings.map((r, i) => (
        <ellipse key={i} cx={HUB[0]} cy={HUB[1]} rx={f1(r.rx)} ry={f1(r.rx * 0.34)} fill="none" stroke={C.coldHot}
          strokeWidth={lerp(2.6, 0.7, r.age)} opacity={0.55 * r.k} />
      ))}

      {/* The complex */}
      {MASTS.map((m, i) => <Mast key={i} {...m} base={PLAT} k={seg(built, 0.16 + i * 0.05, 0.3 + i * 0.05)} ink={ink} t={t} />)}
      {TOWERS.map((tw, i) => (
        <CoolingTower key={i} {...tw} i={i} rise={seg(built, tw.b[0], tw.b[1])} base={PLAT + 2} local={local}
          power={glow} ink={ink} rim={rimCol} />
      ))}
      {CRANES.map((c, i) => (
        <Crane key={i} {...c} base={PLAT} local={local} ink={mixColor("#2a3946", "#0a1016", glow)} beacon={C.coldHot}
          rise={ease.inOut(seg(built, 0, 0.1)) * (1 - ease.inOut(seg(built, 0.88, 0.98)))} />
      ))}
      {built < 1 && LAMPS.map(([x, y], i) => {
        const on = seg(built, 0.02 + i * 0.01, 0.05 + i * 0.01) * (1 - glow);
        return on > 0 && <circle key={i} cx={x} cy={y} r={26} fill="url(#cl-beacon)" opacity={0.7 * on} />;
      })}
      {BLOCKS.map((b) => (
        <g key={b.id} transform={`translate(${b.x} ${b.y}) scale(${b.s})`}>
          <DataCenter id={b.id} x={0} y={0} w={b.w} h={b.h} t={t} label={b.label} fence={false}
            build={0.85 * seg(built, b.b[0], b.b[1]) + 0.15 * seg(built, 0.9, 1)} glow={glow} />
        </g>
      ))}

      {/* The access road and its lamps */}
      {!near && (
        <g>
          <path d={ROAD_D} stroke="#101923" strokeWidth={11} fill="none" strokeLinecap="round" />
          <path d={ROAD_D} stroke={C.cold} strokeWidth={11} fill="none" strokeLinecap="round" opacity={0.08 * glow} />
          <path d={ROAD_LAMPS} fill={C.coldHot} opacity={0.15 + 0.75 * glow} />
        </g>
      )}

      {/* Upper pylons go into the fog */}
      <Run pylons={RUN_L.slice(3)} gantry={GANTRY_L} built={built} ink={ink} wireColor={wire2} />
      <Run pylons={RUN_R.slice(3)} gantry={GANTRY_R} built={built} ink={ink} wireColor={wire2} />

      {/* The fog deck the castle floats on */}
      {FOG.map((f, i) => (
        <ellipse key={i} cx={f1(f.x + Math.sin(local * f.sp + f.ph) * 60)} cy={f.y} rx={f.rx} ry={f.ry}
          fill="url(#cl-fog)" opacity={f.o * (0.55 + 0.45 * glow)} />
      ))}
      <rect x={EXT.x} y={PLAT + 8} width={EXT.w} height={96} fill={fogLit} opacity={0.08 + 0.12 * glow} />
      <path d={BILLOWS} fill="url(#cl-billow)" transform={`translate(${f1(Math.sin(local * 0.05) * 36)} 0)`} opacity={0.45 + 0.55 * glow} />

      {/* Lower pylons, then the town in front (out of shot from the fence) */}
      {!near && (
        <g>
          <Run pylons={RUN_L.slice(0, 4)} gantry={pylonArms(RUN_L[4].x, RUN_L[4].y, RUN_L[4].s)} built={built} ink={ink} wireColor={wire2} />
          <Run pylons={RUN_R.slice(0, 4)} gantry={pylonArms(RUN_R[4].x, RUN_R[4].y, RUN_R[4].s)} built={built} ink={ink} wireColor={wire2} />
          <ellipse cx={800} cy={TOWN_Y + 10} rx={1200} ry={60} fill="url(#cl-townglow)" opacity={(1 - brown) * (1 - 0.6 * dim)} />
          <path d={TOWN.d} fill={ink} />
          <path d={TOWN.legs} stroke={ink} strokeWidth={2.5} fill="none" />
          {TOWN.wins.map((d, i) => {
            const flick = 0.8 + 0.2 * Math.sin(local * 0.7 + i * 2);
            const gone = i === 0 ? seg(dim, 0, 0.4) : i === 1 ? seg(dim, 0.3, 0.8) : seg(dim, 0.7, 1) * 0.7;
            return <path key={i} d={d} fill={i === 1 ? C.warmHot : C.warm} opacity={flick * (1 - 0.85 * brown) * (1 - gone)} />;
          })}
          <rect x={EXT.x} y={TOWN_Y} width={EXT.w} height={2000} fill={ink} />
        </g>
      )}
    </g>
  );
}

const CASTLE_DEFS = (glow) => (
  <>
    <linearGradient id="cl-sky" gradientUnits="userSpaceOnUse" x1="0" y1="-600" x2="0" y2="760">
      <stop offset="0" stopColor="#010203" />
      <stop offset="0.55" stopColor={mixColor("#04070d", "#071019", glow)} />
      <stop offset="1" stopColor={mixColor("#0b121b", "#16263a", glow)} />
    </linearGradient>
    <radialGradient id="cl-dome">
      <stop offset="0" stopColor={C.cold} stopOpacity="0.42" />
      <stop offset="0.45" stopColor={C.accent} stopOpacity="0.14" />
      <stop offset="1" stopColor={C.accent} stopOpacity="0" />
    </radialGradient>
    <radialGradient id="cl-cloud">
      <stop offset="0" stopColor="#6f8aa4" stopOpacity="0.5" />
      <stop offset="1" stopColor="#6f8aa4" stopOpacity="0" />
    </radialGradient>
    <linearGradient id="cl-hill" gradientUnits="userSpaceOnUse" x1="0" y1="362" x2="0" y2="760">
      <stop offset="0" stopColor={mixColor("#0a0f16", "#142231", glow)} />
      <stop offset="0.4" stopColor="#060a0f" />
      <stop offset="1" stopColor="#030508" />
    </linearGradient>
    <radialGradient id="cl-fog">
      <stop offset="0" stopColor={mixColor("#3a4a5a", "#a9bdd0", glow)} stopOpacity="0.75" />
      <stop offset="0.6" stopColor={mixColor("#2a3440", "#7d93a8", glow)} stopOpacity="0.3" />
      <stop offset="1" stopColor="#2a3440" stopOpacity="0" />
    </radialGradient>
    <linearGradient id="cl-billow" gradientUnits="userSpaceOnUse" x1="0" y1={PLAT - 34} x2="0" y2={PLAT + 76}>
      <stop offset="0" stopColor={mixColor("#3a4856", "#c3d3e2", glow)} stopOpacity="0.85" />
      <stop offset="0.45" stopColor={mixColor("#2a3440", "#8ea4b9", glow)} stopOpacity="0.4" />
      <stop offset="1" stopColor="#2a3440" stopOpacity="0" />
    </linearGradient>
    <radialGradient id="cl-steam">
      <stop offset="0" stopColor="#b9c9d8" stopOpacity="0.55" />
      <stop offset="1" stopColor="#b9c9d8" stopOpacity="0" />
    </radialGradient>
    <radialGradient id="cl-beacon">
      <stop offset="0" stopColor={C.coldHot} stopOpacity="0.8" />
      <stop offset="1" stopColor={C.coldHot} stopOpacity="0" />
    </radialGradient>
    <radialGradient id="cl-townglow">
      <stop offset="0" stopColor={C.warm} stopOpacity="0.28" />
      <stop offset="1" stopColor={C.warm} stopOpacity="0" />
    </radialGradient>
  </>
);

// ---------------------------------------------------------------------------
// Shot 2 · The castle in the clouds. The complex goes up on the hill over the
// old town and switches on; then his open hand rises in front of it.
// ---------------------------------------------------------------------------

// His right hand, palm to us, fingers up; origin at the wrist. Thick
// fingers, big joints: a working hand.
const PALM_D =
  "M -120 0 C -128 -60 -148 -120 -152 -190 C -156 -240 -152 -276 -144 -298 " +
  "Q -116 -316 -76 -312 Q -38 -328 0 -330 Q 50 -334 88 -322 Q 118 -318 130 -306 " +
  "C 142 -270 150 -226 152 -180 C 156 -120 140 -60 120 0 Z";
const DIGITS = [
  { x: -112, y: -290, w: 56, a: -0.18, len: [74, 50, 44] },
  { x: -42, y: -314, w: 64, a: -0.06, len: [100, 68, 56] },
  { x: 34, y: -322, w: 67, a: 0.03, len: [108, 74, 60] },
  { x: 104, y: -308, w: 64, a: 0.13, len: [96, 66, 54] },
];
const fingerPts = (f, curl) => {
  const pts = [[f.x, f.y + 30], [f.x, f.y]];
  let a = f.a;
  let [x, y] = [f.x, f.y];
  f.len.forEach((l, i) => {
    a += i ? curl * 0.1 : 0;
    x += Math.sin(a) * l;
    y -= Math.cos(a) * l * (1 - curl * 0.08 * i);
    pts.push([x, y]);
  });
  return pts;
};
const THUMB_D = taper([[96, -70], [130, -116], [196, -184], [236, -248], [258, -298]], [96, 84, 70, 62, 56]);
const HAND_LINES = [
  "M -140 -236 C -80 -250 0 -262 64 -300", // heart line
  "M -136 -176 C -70 -190 10 -202 92 -232", // head line
  "M 100 -238 C 56 -186 44 -112 60 -30", // life line round the thumb
].join(" ");
const CALLUS = [
  [-108, -292, 30, 14], [-40, -316, 34, 15], [34, -324, 36, 15], [102, -308, 32, 14],
  [-104, -64, 46, 32], [160, -150, 34, 24],
];
const WRIST_D = "M -120 0 L -150 1500 L 160 1500 L 122 0 Z";

function OpenHand({ x, y, s, rot, curl, rim, warm }) {
  const fingers = DIGITS.map((f) => {
    const pts = fingerPts(f, curl);
    return taper(pts, [f.w, f.w, f.w * 0.95, f.w * 0.88, f.w * 0.8]);
  });
  const layer = (color, grow, key, opacity, dx = 0, dy = 0) => (
    <g key={key} fill={color} stroke={color} strokeWidth={grow} strokeLinejoin="round" opacity={opacity}
      transform={dx || dy ? `translate(${dx} ${dy})` : undefined}>
      <path d={WRIST_D} />
      <path d={PALM_D} />
      <path d={THUMB_D} />
      {fingers.map((d, i) => <path key={i} d={d} />)}
    </g>
  );
  const pts = DIGITS.map((f) => fingerPts(f, curl));
  const tips = pts.map((p) => p[4]);
  // Creases across each finger at its two joints.
  const creases = pts.map((p, i) => [2, 3].map((j) => {
    const [x0, y0] = p[j - 1];
    const [x1, y1] = p[j];
    const len = Math.hypot(x1 - x0, y1 - y0) || 1;
    const nx = -(y1 - y0) / len;
    const ny = (x1 - x0) / len;
    const w = DIGITS[i].w * 0.26;
    return `M ${f1(x1 - nx * w)} ${f1(y1 - ny * w)} L ${f1(x1 + nx * w)} ${f1(y1 + ny * w)}`;
  }).join(" ")).join(" ");
  return (
    <g transform={`translate(${f1(x)} ${f1(y)}) rotate(${rot.toFixed(2)}) scale(${s})`}>
      {layer(C.cold, 14, "glow", 0.14 * rim)}
      {layer(C.coldHot, 4, "rim", 0.75 * rim, 2.5, -2)}
      {layer("#05070a", 0.01, "ink", 1)}
      {/* Worn places: calluses and creases hold the last of the warm */}
      <g opacity={warm}>
        {CALLUS.map(([cx, cy, rx, ry], i) => (
          <ellipse key={i} cx={cx} cy={cy} rx={rx} ry={ry} fill="url(#cl-callus)" />
        ))}
        {tips.map(([tx, ty], i) => <ellipse key={`t${i}`} cx={f1(tx)} cy={f1(ty + 14)} rx={20} ry={13} fill="url(#cl-callus)" />)}
        <path d={HAND_LINES} stroke="#24170e" strokeWidth={3} fill="none" opacity={0.8} strokeLinecap="round" />
        <path d={creases} stroke="#1c120b" strokeWidth={2.5} fill="none" opacity={0.7} strokeLinecap="round" />
      </g>
    </g>
  );
}

function CastleShot({ q, local, t }) {
  const built = seg(q, 0, 0.36);
  const ignite = seg(q, 0.3, 0.335);
  // It catches with a stutter, then holds.
  const glow = ignite >= 1
    ? lerp(0.85, 1, seg(q, 0.335, 0.6))
    : ignite > 0 && noise(Math.floor(local * 15)) < 0.2 + ignite ? 0.3 + 0.55 * ignite : 0;
  const hum = smooth(seg(q, 0.32, 0.5));
  const brown = ignite > 0 && ignite < 1 ? (glow > 0 ? 0.9 : 0.35) : ignite >= 1 ? 0.55 * (1 - seg(q, 0.335, 0.4)) : 0;
  const dim = seg(q, 0.45, 1);
  const flash = ignite >= 1 ? Math.max(0, 1 - (q - 0.335) / 0.03) : 0;
  const zoom = lerp(1, 1.16, smooth(clamp(q)));
  const cam = `translate(1000 560) scale(${zoom.toFixed(4)}) translate(-1000 -560)`;

  // His hand comes up into the light, holds, and late on sinks a little.
  const rise = ease.out(seg(q, 0.5, 0.6));
  const sink = ease.inOut(seg(q, 0.9, 1));
  const handY = 960 + 820 * (1 - rise) + 160 * sink + Math.sin(local * 0.6) * 4;
  const handRot = -6 + Math.sin(local * 0.4) * 0.6 + 3 * sink;

  return (
    <g>
      <defs>
        {CASTLE_DEFS(glow)}
        <radialGradient id="cl-callus">
          <stop offset="0" stopColor="#4a2c16" stopOpacity="0.5" />
          <stop offset="1" stopColor="#4a2c16" stopOpacity="0" />
        </radialGradient>
      </defs>
      <g transform={cam}>
        <Castle built={built} glow={glow} hum={hum} brown={brown} dim={dim} local={local} t={t} />
        {flash > 0 && <ellipse cx={HUB[0]} cy={HUB[1]} rx={900} ry={420} fill="url(#cl-dome)" opacity={flash} />}
      </g>
      {rise > 0 && (
        <OpenHand x={470} y={handY} s={1.2} rot={handRot} curl={0.15} rim={0.5 + 0.5 * glow} warm={0.9} />
      )}
    </g>
  );
}

// ---------------------------------------------------------------------------
// Shot 3 · Lights out. Inside: racks down both walls, a line of arms working
// in the dark, LEDs, sparks, and nobody there.
// ---------------------------------------------------------------------------

const HVX = 800;
const HVY = 404;
const HG = 390;
const HP = (X, Z, h = 0) => [HVX + X / Z, HVY + (HG - h) / Z];
const RACK_X = 640;
const RACK_H = 540;
const HFAR = 16;
const RACKS = (() => {
  const out = [];
  for (let z = 1.05; z < HFAR - 0.6; z += 0.82) out.push([z, z + 0.66]);
  return out;
})();
const HALL = (() => {
  const faces = { L: "", R: "" };
  const frames = { L: "", R: "" };
  RACKS.forEach(([z0, z1]) => {
    ["L", "R"].forEach((side) => {
      const X = side === "L" ? -RACK_X : RACK_X;
      faces[side] += poly([HP(X, z0, 0), HP(X, z1, 0), HP(X, z1, RACK_H), HP(X, z0, RACK_H)]) + " ";
      const a = HP(X, z0, 0);
      const b = HP(X, z0, RACK_H);
      frames[side] += `M ${f1(a[0])} ${f1(a[1])} L ${f1(b[0])} ${f1(b[1])} `;
    });
  });
  const leds = ["", "", "", "", "", ""];
  const r = rng(5150);
  RACKS.forEach(([z0, z1]) => {
    if (z0 > 9) return;
    ["L", "R"].forEach((side) => {
      const X = side === "L" ? -RACK_X : RACK_X;
      for (let z = z0 + 0.09; z < z1 - 0.06; z += 0.12) {
        for (let h = 50; h < RACK_H - 30; h += 38) {
          if (r() < 0.25) continue;
          const [x, y] = HP(X, z, h + r() * 8);
          const s = Math.max(0.8, 4.6 / z);
          leds[Math.floor(r() * leds.length)] += sq(x, y, s);
        }
      }
    });
  });
  const seg2 = (a, b) => `M ${f1(a[0])} ${f1(a[1])} L ${f1(b[0])} ${f1(b[1])} `;
  let ceil = "";
  [-420, -140, 140, 420].forEach((X) => { ceil += seg2(HP(X, 0.9, 700), HP(X, HFAR, 700)); });
  [1.6, 2.4, 3.5, 5.1, 7.4, 10.8].forEach((z) => { ceil += seg2(HP(-RACK_X, z, 700), HP(RACK_X, z, 700)); });
  let floor = "";
  [-300, -150, 150, 300].forEach((X) => { floor += seg2(HP(X, 0.5), HP(X, HFAR)); });
  const [bx0, by0] = HP(-RACK_X, HFAR, 760);
  const [bx1, by1] = HP(RACK_X, HFAR, 0);
  return {
    faces, frames, leds, ceil, floor,
    floorD: poly([HP(-RACK_X, HFAR), HP(RACK_X, HFAR), HP(RACK_X, 0.3), HP(-RACK_X, 0.3)]),
    wallL: poly([HP(-RACK_X, 0.4, 760), HP(-RACK_X, HFAR, 760), HP(-RACK_X, HFAR), HP(-RACK_X, 0.4)]),
    wallR: poly([HP(RACK_X, 0.4, 760), HP(RACK_X, HFAR, 760), HP(RACK_X, HFAR), HP(RACK_X, 0.4)]),
    back: { x: bx0, y: by0, w: bx1 - bx0, h: by1 - by0 },
    belt: poly([HP(-80, 0.95, 110), HP(80, 0.95, 110), HP(80, HFAR, 110), HP(-80, HFAR, 110)]),
    beltSide: poly([HP(80, 0.95, 110), HP(80, HFAR, 110), HP(80, HFAR, 70), HP(80, 0.95, 70)]),
    beltSideL: poly([HP(-80, 0.95, 110), HP(-80, HFAR, 110), HP(-80, HFAR, 70), HP(-80, 0.95, 70)]),
  };
})();
const LED_COLORS = [C.accent, C.cold, C.coldHot, C.accent, C.cold, "#7fa6c9"];
const LED_RATE = [0.7, 1.9, 3.1, 1.3, 2.4, 0.45];
const HALL_ARMS = [1.3, 1.85, 2.6, 3.6, 5.0, 7.0, 9.6].map((z, i) => ({
  z, X: i % 2 ? 230 : -230, facing: i % 2 ? -1 : 1, ph: i * 0.37,
}));
const STRIPS = (() => {
  const seg2 = (a, b) => `M ${f1(a[0])} ${f1(a[1])} L ${f1(b[0])} ${f1(b[1])} `;
  return [-RACK_X, RACK_X].map((X) => seg2(HP(X, 1.0, RACK_H + 6), HP(X, HFAR, RACK_H + 6)) + seg2(HP(X, 1.0, 22), HP(X, HFAR, 22))).join(" ");
})();
// Spark streaks from a weld: [vx, vy] in px/s at s = 1.
const SPARKS = (() => {
  const r = rng(1414);
  return Array.from({ length: 8 }, () => {
    const a = -Math.PI * (0.1 + 0.8 * r());
    const v = 140 + r() * 240;
    return [Math.cos(a) * v, Math.sin(a) * v * 0.8];
  });
})();

function HallShot({ q, local, t }) {
  const zoom = lerp(1, 1.16, smooth(clamp(q)));
  const cam = `translate(${HVX} ${HVY}) scale(${zoom.toFixed(4)}) translate(${-HVX} ${-HVY})`;
  const sparks = [];
  const arms = HALL_ARMS.slice().sort((a, b) => b.z - a.z).map((a, i) => {
    const s = 1.75 / a.z;
    const [x, y] = HP(a.X, a.z);
    const L = local / 1.05 + a.ph;
    const f = frac(L);
    const pose = armCycle(f);
    ARM_HITS.forEach((h, hi) => {
      const age = (f - h) * 1.05;
      if (age < 0 || age > 0.4 || noise(Math.floor(L) * 3.7 + i * 1.9 + hi * 5.3) > 0.6) return;
      const tip = armTip(hi ? ARM_POSES.bHit : ARM_POSES.aHit);
      const ox = x + tip[0] * s * a.facing;
      const oy = y + tip[1] * s;
      const fade = 1 - age / 0.4;
      sparks.push(<circle key={`g${i}-${hi}`} cx={f1(ox)} cy={f1(oy)} r={f1(90 * s)} fill="url(#cl-spark)" opacity={fade} />);
      SPARKS.forEach(([vx, vy], j) => {
        const px = ox + vx * age * s * a.facing;
        const py = oy + (vy * age + 260 * age * age) * s;
        sparks.push(
          <line key={`s${i}-${hi}-${j}`} x1={f1(px)} y1={f1(py)} x2={f1(px - vx * 0.03 * s * a.facing)} y2={f1(py - (vy + 520 * age) * 0.03 * s)}
            stroke={C.coldHot} strokeWidth={f1(Math.max(0.8, 1.8 * s))} strokeLinecap="round" opacity={fade} />,
        );
      });
    });
    return <Arm key={i} x={x} y={y} s={s} facing={a.facing} pose={pose} lit={0.08} led={1} />;
  });
  // Drives ride the belt out toward us.
  const boxes = [];
  for (let k = 0; k < 18; k++) {
    const z = 1 + ((k * 0.7 - local * 0.9) % 12.6 + 12.6) % 12.6;
    const [bx, by] = HP(0, z, 110);
    boxes.push(<rect key={k} x={f1(bx - 34 / z)} y={f1(by - 18 / z)} width={f1(68 / z)} height={f1(18 / z)} fill="#0e151c" stroke={C.cold} strokeOpacity={0.35} strokeWidth={0.8} />);
  }
  const typed = (text, k) => text.slice(0, Math.round(text.length * clamp(k)));
  return (
    <g>
      <defs>
        <radialGradient id="cl-spark">
          <stop offset="0" stopColor={C.coldHot} stopOpacity="0.9" />
          <stop offset="0.25" stopColor={C.cold} stopOpacity="0.3" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="cl-hall-floor" gradientUnits="userSpaceOnUse" x1="0" y1={HVY} x2="0" y2="900">
          <stop offset="0" stopColor="#0b1520" />
          <stop offset="1" stopColor="#020407" />
        </linearGradient>
        <linearGradient id="cl-hall-wash" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={C.accent} stopOpacity="0.16" />
          <stop offset="0.3" stopColor={C.accent} stopOpacity="0" />
          <stop offset="0.7" stopColor={C.accent} stopOpacity="0" />
          <stop offset="1" stopColor={C.accent} stopOpacity="0.16" />
        </linearGradient>
      </defs>
      <g transform={cam}>
        <rect x={EXT.x} y={EXT.y} width={EXT.w} height={EXT.h} fill="#020305" />
        <path d={HALL.floorD} fill="url(#cl-hall-floor)" />
        <path d={HALL.wallL} fill="#03060a" />
        <path d={HALL.wallR} fill="#03060a" />
        <rect x={HALL.back.x} y={HALL.back.y} width={HALL.back.w} height={HALL.back.h} fill="#050a10" />
        <path d={HALL.ceil} stroke="#0d1620" strokeWidth={2} fill="none" />
        <path d={HALL.floor} stroke="#0b141d" strokeWidth={1} fill="none" />
        <path d={HALL.faces.L} fill="#05090e" />
        <path d={HALL.faces.R} fill="#05090e" />
        <path d={HALL.frames.L + HALL.frames.R} stroke="#13202d" strokeWidth={1.5} fill="none" />
        {HALL.leds.map((d, i) => {
          const on = Math.sin(t * LED_RATE[i] + i * 1.7) > -0.2 ? 1 : 0.18;
          return <path key={i} d={d} fill={LED_COLORS[i]} opacity={on * (i === 2 ? 0.9 : 0.75)} />;
        })}
        <rect x={EXT.x} y={HVY - 200} width={EXT.w} height={800} fill="url(#cl-hall-wash)" />
        <path d={STRIPS} stroke={C.accent} strokeWidth={2} fill="none" opacity={0.55} />
        <path d={STRIPS} stroke={C.cold} strokeWidth={8} fill="none" opacity={0.06} />

        {/* The line */}
        <path d={HALL.beltSideL} fill="#060b10" />
        <path d={HALL.beltSide} fill="#060b10" />
        <path d={HALL.belt} fill="#0a1118" />
        {boxes}
        {arms}
        {sparks}
      </g>

      {/* Telemetry */}
      <g>
        <path d="M 196 214 V 300" stroke={C.accent} strokeWidth={1.5} opacity={seg(q, 0.08, 0.1)} />
        <Label x={212} y={230} size={15} color={C.cold}>{typed("HALL 3 · LIGHTS OUT", (q - 0.1) / 0.06)}</Label>
        <Label x={212} y={262} size={15} color={C.cold}>{typed("STAFF ON FLOOR  0", (q - 0.2) / 0.06)}</Label>
        <Label x={212} y={294} size={15} color={C.cold}>{typed("UPTIME 100.00%", (q - 0.3) / 0.06)}</Label>
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// Shot 4 · The fence. He stands at the wire under the castle and fades out of
// the picture. A blank picket sign lies face down in the mud. Nothing up
// there changes.
// ---------------------------------------------------------------------------

const FY = 712; // foot of the perimeter fence
const FH = 176;
const POSTS = Array.from({ length: 16 }, (_, i) => -380 + i * 160);
const MUD = (() => {
  const r = rng(321);
  let d = "";
  for (let i = 0; i < 26; i++) {
    const x = -380 + r() * 2360;
    const y = 740 + r() * 140;
    d += oval(x, y, 20 + r() * 70, 2 + r() * 3);
  }
  return d;
})();

function FenceShot({ q, local, t }) {
  const cam = `translate(900 330) scale(${lerp(1.45, 1.5, smooth(clamp(q))).toFixed(4)}) translate(-1000 -354)`;
  const fade = ease.inOut(seg(q, 0.38, 0.88));
  const pose = q < 0.3 ? "lookUp" : "stand";
  const top = FY - FH;
  return (
    <g>
      <defs>
        {CASTLE_DEFS(1)}
        <pattern id="cl-mesh" width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <path d="M 0 0 V 12 M 0 0 H 12" stroke="#8fa6bb" strokeWidth="0.8" fill="none" />
        </pattern>
        <linearGradient id="cl-mud" gradientUnits="userSpaceOnUse" x1="0" y1="640" x2="0" y2="900">
          <stop offset="0" stopColor="#0b121a" />
          <stop offset="1" stopColor="#020305" />
        </linearGradient>
        <radialGradient id="cl-puddle">
          <stop offset="0" stopColor={C.cold} stopOpacity="0.4" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0" />
        </radialGradient>
      </defs>
      <g transform={cam}>
        <Castle built={1} glow={1} hum={1} brown={0} dim={1} local={local} t={t} near />
      </g>

      {/* The near field: mud, the wire, him, the sign */}
      <path d="M -400 690 Q 300 676 800 684 T 2000 680 V 2700 H -400 Z" fill="url(#cl-mud)" />
      <path d={MUD} fill={C.cold} opacity={0.08} />
      <rect x={-400} y={top} width={2400} height={FH} fill="url(#cl-mesh)" opacity={0.22} />
      <path d={POSTS.map((x) => `M ${x} ${FY} V ${top - 14} L ${x + 10} ${top - 24}`).join(" ")} stroke="#020304" strokeWidth={5} fill="none" />
      <path d={`M -400 ${top} H 2000 M -400 ${FY - 4} H 2000`} stroke="#020304" strokeWidth={3} />
      <path d={`M -400 ${top - 10} H 2000 M -400 ${top - 19} H 2000`} stroke="#020304" strokeWidth={1.2} />

      {/* The protest that never came: a blank sign face down in a puddle */}
      <ellipse cx={880} cy={752} rx={230} ry={22} fill="#04070a" />
      <ellipse cx={880} cy={752} rx={210} ry={15} fill="url(#cl-puddle)" />
      <g transform="translate(860 744) rotate(-5)">
        <path d="M -96 -12 L 76 -20 L 102 8 L -72 16 Z" fill="#4c4a44" />
        <path d="M -96 -12 L 76 -20 L 102 8 L -72 16 Z" fill="none" stroke="#14130f" strokeWidth={2} />
        <path d="M -72 16 L 102 8 L 102 12 L -72 20 Z" fill="#1f1d19" />
        <path d="M -40 -2 Q 0 -6 40 -8" stroke="#2a2822" strokeWidth={3} fill="none" opacity={0.6} />
        <path d="M 84 -4 L 278 14" stroke="#3a2c1d" strokeWidth={9} strokeLinecap="round" />
        <path d="M 84 -4 L 278 14" stroke={C.cold} strokeWidth={1.5} opacity={0.25} transform="translate(0 -3)" />
      </g>
      <path d={oval(780, 770, 26, 3) + oval(990, 766, 18, 2.5) + oval(700, 760, 12, 2)} fill="#020305" opacity={0.8} />

      {/* Him, rimmed by the cold, going */}
      <g opacity={1 - fade}>
        <g transform="translate(2.5 -2)" opacity={0.55}>
          <Worker x={560} y={744} s={1.32} facing={1} pose={pose} color={C.cold} hat="cap" />
        </g>
        <Worker x={560} y={744} s={1.32} facing={1} pose={pose} color={C.ink[0]} hat="cap" />
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// The edit
// ---------------------------------------------------------------------------

// xf: seconds of dissolve into the shot; 0 is a hard cut.
const SHOTS = [
  { at: 0, Shot: PourShot },
  { at: 0.075, Shot: CastleShot, xf: 0.6 },
  { at: 0.52, Shot: HallShot, xf: 0 },
  { at: 0.8, Shot: FenceShot, xf: 0.4 },
];

export default function TheCloud({ p, t, local, dur }) {
  return (
    <g>
      {SHOTS.map(({ at, Shot, xf = 0 }, i) => {
        const half = xf / 2 / dur;
        const next = SHOTS[i + 1];
        const end = next ? next.at : 1;
        if (i > 0 && p < at - half) return null;
        if (next && p >= end + (next.xf || 0) / 2 / dur) return null;
        const alpha = i === 0 ? 1 : half > 0 ? smooth(seg(p, at - half, at + half)) : 1;
        return (
          <g key={i} opacity={alpha}>
            <Shot q={(p - at) / (end - at)} len={(end - at) * dur} local={local} t={t} />
          </g>
        );
      })}
    </g>
  );
}
